import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {TranslationKey, useTranslation} from 'i18n'
import {
  GiftShopBannerReferralTemplate,
  GiftShopTemplateRoboyuModalExpMission,
} from 'pages'
import {parseShape} from 'types'
import {
  getRoboyuMaxExp,
  useDidMount,
  useDidUpdate,
  useFirestoreRoboyuStatus,
  useHistory,
  useLocation,
  useRoboyuCustomizationHooks,
} from 'utils'
import {useTheme} from 'themes'
import {
  ButtonIcon,
  ButtonOutline,
  CircularProgressBar,
  Icon,
  Paragraph,
  ParsedText,
  RoboyuConfused,
  RoboyuFly,
} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {handleGiftshopGetAccount} from 'repositories'
import {GiftShopRetakeKYCModal} from '../../../explore'
import {
  GiftShopRobopetActivationIncomplete,
  GiftShopRobopetActivationProcessing,
  GiftShopRobopetActivationRejected,
  GiftShopRobopetActivationUnregistered,
} from '../ActivationItem'

interface StyledBannerContainerProps {
  bannerClosed?: boolean
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertUnit(20)};
  overflow-y: scroll;
`

const StyledUpperSectionContainer = styled.div`
  display: flex;
  flex-direction: 'row';
  justify-content: flex-start;

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    flex-direction: column;
  }
`

const StyledSearchAccuracySectionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: center;
  gap: ${convertUnit(20)};
  padding: 0 ${convertUnit(40)};

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: inherit;
    padding: 0 ${convertUnit(20)};
    padding-bottom: 0;
  }
`

// const StyledAccuracyTitleContainer = styled.div`
//   padding-bottom: ${convertUnit(8)};
//   text-align: center;
// `

// const StyledAccuracyDescContainer = styled.div`
//   display: flex;
//   flex-direction: column;
// `

const StyledRobopetImageContainer = styled.div`
  width: 100%;
  max-width: ${convertUnit(88)};
  aspect-ratio: 1;
  object-fit: contain;
  margin-right: ${convertUnit(12)};
`

const StyledAccuracyDisabledContainer = styled.div`
  display: flex;
  background-color: ${({theme}) => theme.info_1};
  border-radius: ${convertUnit(16)};
  padding: ${convertUnit(24)};
  width: fill-available;
  margin-top: ${convertUnit(20)};
`

const StyledAccuracyDisabledText = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledRoboyuFindingsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertUnit(8)};
  text-align: left;
  padding: ${convertUnit(20)} ${convertUnit(40)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    padding: ${convertUnit(20)};
  }
`

const StyledBannerContainer = styled.div<StyledBannerContainerProps>`
  visibility: ${({bannerClosed}) => (bannerClosed ? 'hidden' : 'visible')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 ${convertUnit(40)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: inherit;
    margin: 0 ${convertUnit(20)};
  }
`

const StyledRoboyuExpContainer = styled(StyledSearchAccuracySectionContainer)`
  text-align: left;
  justify-content: center;
  align-items: center;
`
const StyledRoboyuExpContentContainer = styled.div`
  border-radius: ${convertUnit(16)};
  border: ${convertUnit(3)} solid ${({theme}) => theme.primary_5};
  height: fit-content;
  padding: ${convertUnit(20)};
  width: fill-available;
`
const StyledRoboyuTopContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
`
const StyledRoboyuButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: fill-available;
  gap: ${convertUnit(8)};
  margin-top: ${convertUnit(8)};
`
const StyledRoboyuLevelProgressContainer = styled.div`
  display: flex;
  position: relative;
`
const StyledRoboyuLevelProgressOverlayContainer = styled.div`
  width: ${convertUnit(40)};
  height: ${convertUnit(40)};
  display: flex;
  position: absolute;
  justify-content: center;
`
const StyledRoboyuLevelTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(8)};
`
const StyledRoboyuLevelContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`
const StyledRoboyuExpText = styled(Paragraph)`
  display: flex;
  align-items: center;
`
export default function GiftShopRoboPetContent() {
  const {kycStatus} = useSelector('yuserActivationState')
  const history = useHistory()
  const {afterKyc} = useLocation('giftshop_robopet').state || {}
  const {translate} = useTranslation()
  // const {sensitivityLevel} = useSelector('roboyuSensitivityLevel')
  // const stateRangeValue = useState(sensitivityLevel)
  const bannerClosed = useSelector('bannerClosed')
  const isVerified = useMemo(
    () => kycStatus === 'verified' || kycStatus === 'verified_check',
    [kycStatus],
  )
  const stateProgress = useState(0)
  const setProgress = stateProgress[1]
  const {colorTheme, lv: level, exp} = useSelector('roboyuState')
  const theme = useTheme()
  const [maxExp, setMaxExp] = useState(0)
  const [modal, setModal] = useState<'retake' | 'exp' | undefined>(undefined)
  const {handleFetchRoboyuCustomizationStatus} = useRoboyuCustomizationHooks()

  const handleGetStatus = useCallback(async () => {
    const account = await handleGiftshopGetAccount()
    if (!account) return

    if (account.retake_status === 'required') {
      setModal('retake')
      return
    }
    if (account.status === 'onhold') {
      history.replace('giftshop_similar_identity', {
        afterKyc,
        fromRoboyu: true,
      })
    }
  }, [afterKyc, history])

  // const handlePatchSensitivityLevel = useCallback(
  //   (accuracy: number) => {
  //     requestData('giftshop_patch_sensitivity_level', {
  //       data: {sensitivity_level: accuracy},
  //       onRequestSuccess: ({status}) => {
  //         if (status === 200) {
  //           update('roboyuSensitivityLevel', {sensitivityLevel: accuracy})
  //           showSnackbar(translate('giftShop:roboyuAccuracySuccessSnackbar'))
  //         }
  //       },
  //     })
  //   },
  //   [translate, update],
  // )

  const handleRenderRetakeKYCModal = useMemo(
    () => (
      <GiftShopRetakeKYCModal visible toggleModal={() => setModal(undefined)} />
    ),
    [],
  )

  const handleRenderCustomRoboyu = useMemo(
    () => (
      <StyledRoboyuExpContainer>
        <StyledRoboyuExpContentContainer>
          <StyledRoboyuTopContainer>
            <RoboyuFly
              colorTheme={colorTheme.name}
              width={convertUnit(160)}
              height={convertUnit(160)}
            />
            <StyledRoboyuLevelContainer>
              <StyledRoboyuLevelTextContainer>
                <StyledRoboyuLevelProgressContainer>
                  <CircularProgressBar
                    gradientStart={theme.info_4}
                    gradientStop={theme.primary_6}
                    width={40}
                    strokeWidth={6}
                    strokeColor={theme.gray_1}
                    stateProgress={stateProgress}
                  />
                  <StyledRoboyuLevelProgressOverlayContainer>
                    <Icon type="robopet-alt" color="primary_5" size={16} />
                  </StyledRoboyuLevelProgressOverlayContainer>
                </StyledRoboyuLevelProgressContainer>
                <StyledRoboyuLevelContainer>
                  <Paragraph fontSize="l" fontWeight="bold">
                    {`Level ${level}`}
                    <Paragraph color="primary_5" fontWeight="medium">
                      <Icon type="robopet-alt" size={12} color="primary_5" />
                      {`${exp} / ${maxExp}`}
                    </Paragraph>
                  </Paragraph>
                </StyledRoboyuLevelContainer>
              </StyledRoboyuLevelTextContainer>
              <StyledRoboyuExpText color="info_6" fontWeight="medium">
                <Icon type="double-arrow-up" size={16} color="info_6" />
                {translate('giftShop:roboyuExp', {
                  exp: maxExp === 0 ? '-' : `${maxExp - exp}`,
                })}
              </StyledRoboyuExpText>
              <StyledRoboyuButtonContainer>
                <ButtonIcon
                  iconType="pencil"
                  iconColor="white_1"
                  type="smallest"
                  disabled={maxExp === 0}
                  iconSize={12}
                  style={{padding: 0, minWidth: convertUnit(28)}}
                  hasShadow={false}
                  onClick={() => history.push('giftshop_customize_roboyu', {})}
                />
                <ButtonOutline
                  label={translate('giftShop:roboyuExpMissions')}
                  color="primary_5"
                  type="smallest"
                  fontSize="s"
                  disabled={maxExp === 0}
                  borderColor="primary_5"
                  borderHoverColor="primary_5"
                  onClick={() => setModal('exp')}
                />
              </StyledRoboyuButtonContainer>
            </StyledRoboyuLevelContainer>
          </StyledRoboyuTopContainer>
          <Paragraph fontSize="m">
            {translate('giftShop:roboyuActivationVerifiedDescription')}
          </Paragraph>
        </StyledRoboyuExpContentContainer>
      </StyledRoboyuExpContainer>
    ),
    [
      colorTheme,
      exp,
      history,
      level,
      maxExp,
      stateProgress,
      theme.gray_1,
      theme.info_4,
      theme.primary_6,
      translate,
    ],
  )

  const handleRenderContent = useMemo(() => {
    switch (kycStatus) {
      case 'incomplete':
        return <GiftShopRobopetActivationIncomplete />
      case 'processing':
        return <GiftShopRobopetActivationProcessing />
      case 'rejected':
        return <GiftShopRobopetActivationRejected />
      case 'unregistered':
        return <GiftShopRobopetActivationUnregistered />
      case 'verified':
        return handleRenderCustomRoboyu
      case 'verified_check':
        return handleRenderCustomRoboyu
    }
    return null
  }, [handleRenderCustomRoboyu, kycStatus])

  // const handleRenderInputRangeLeftDesc = useMemo(
  //   () => (
  //     <StyledAccuracyDescContainer style={{textAlign: 'left'}}>
  //       <Paragraph fontSize="m" fontWeight="bold">
  //         {translate('giftShop:roboyuAccuracyMediumDesc')}
  //       </Paragraph>
  //       <Paragraph fontWeight="medium">{translate('global:medium')}</Paragraph>
  //     </StyledAccuracyDescContainer>
  //   ),
  //   [translate],
  // )

  // const handleRenderInputRangeRightDesc = useMemo(
  //   () => (
  //     <StyledAccuracyDescContainer style={{textAlign: 'right'}}>
  //       <Paragraph fontSize="m" fontWeight="bold">
  //         {translate('giftShop:roboyuAccuracyHighDesc')}
  //       </Paragraph>
  //       <Paragraph fontWeight="medium">{translate('global:high')}</Paragraph>
  //     </StyledAccuracyDescContainer>
  //   ),
  //   [translate],
  // )

  // const handleRenderInputRange = useMemo(
  //   () => (
  //     <InputRange
  //       showTick
  //       stateValue={stateRangeValue}
  //       min={ROBOYU_SENSITIVITY_LEVEL_MIN}
  //       max={ROBOYU_SENSITIVITY_LEVEL_MAX}
  //       leftDesc={handleRenderInputRangeLeftDesc}
  //       rightDesc={handleRenderInputRangeRightDesc}
  //       onSlide={(val) => handlePatchSensitivityLevel(val)}
  //     />
  //   ),
  //   [
  //     handlePatchSensitivityLevel,
  //     handleRenderInputRangeLeftDesc,
  //     handleRenderInputRangeRightDesc,
  //     stateRangeValue,
  //   ],
  // )

  const handleRenderBanner = useMemo(
    () => (
      <StyledBannerContainer bannerClosed={bannerClosed.roboyu}>
        <GiftShopBannerReferralTemplate page="roboYu" />
      </StyledBannerContainer>
    ),
    [bannerClosed.roboyu],
  )

  // const handleRenderSearchAccuracy = useMemo(
  //   () => (
  //     <StyledSearchAccuracySectionContainer>
  //       <StyledAccuracyTitleContainer>
  //         <Paragraph fontSize="l" fontWeight="bold">
  //           {translate('giftShop:roboyuAccuracyTitle')}
  //         </Paragraph>
  //       </StyledAccuracyTitleContainer>
  //       {handleRenderInputRange}
  //       <GiftShopRoboPetButton />
  //     </StyledSearchAccuracySectionContainer>
  //   ),
  //   [handleRenderInputRange, translate],
  // )

  const handleRenderSearchAccuracyDisabled = useMemo(
    () => (
      <StyledBannerContainer>
        <StyledAccuracyDisabledContainer>
          <StyledRobopetImageContainer>
            <RoboyuConfused
              colorTheme={colorTheme.name}
              width={convertUnit(88)}
              height={convertUnit(88)}
            />
          </StyledRobopetImageContainer>
          <StyledAccuracyDisabledText>
            <Paragraph fontSize="l" fontWeight="bold" color="primary_5">
              {translate('giftShop:roboyuAccuracyTitle')}
            </Paragraph>
            <Paragraph fontSize="m" color="primary_5">
              {translate('giftShop:roboyuAccuracyDisabled')}
            </Paragraph>
          </StyledAccuracyDisabledText>
        </StyledAccuracyDisabledContainer>
      </StyledBannerContainer>
    ),
    [colorTheme.name, translate],
  )

  const roboyuFindingTranslationKeys: TranslationKey[] = useMemo(
    () => [
      'giftShop:searchResultDisclaimer1',
      'giftShop:searchResultDisclaimer2',
      'giftShop:searchResultDisclaimer3',
      'giftShop:searchResultDisclaimer4',
      'giftShop:searchResultDisclaimer5',
      'giftShop:searchResultDisclaimer6',
      'giftShop:searchResultDisclaimer7',
      'giftShop:searchResultDisclaimer8',
    ],
    [],
  )

  const patterns: parseShape[] = useMemo(() => {
    const regex = /\*\*(.+?)\*\*/
    return [
      {
        pattern: regex,
        style: {fontFamily: 'roboto-bold'},
        renderText: (text) => text.replace(regex, `$1`),
      },
    ]
  }, [])

  const handleRenderRoboyuFindings = useMemo(
    () => (
      <StyledRoboyuFindingsSection>
        <Paragraph fontSize="xl" fontWeight="bold">
          {translate('giftShop:searchResultDisclaimerTitle')}
        </Paragraph>
        {roboyuFindingTranslationKeys.map((key) => (
          <>
            <Paragraph fontSize="m" fontWeight="regular" lineHeight={26}>
              <ParsedText parse={patterns}>{translate(key)}</ParsedText>
            </Paragraph>
            <br />
          </>
        ))}
        <Paragraph fontSize="l" fontWeight="medium" lineHeight={26}>
          <ParsedText parse={patterns}>
            {translate('giftShop:searchResultDisclaimerCloser')}
          </ParsedText>
        </Paragraph>
      </StyledRoboyuFindingsSection>
    ),
    [patterns, roboyuFindingTranslationKeys, translate],
  )

  const handleRenderExpModal = useMemo(
    () => (
      <GiftShopTemplateRoboyuModalExpMission
        toggleModal={() => setModal(undefined)}
      />
    ),
    [],
  )

  const handleRenderModal = useMemo(() => {
    switch (modal) {
      case 'retake':
        return handleRenderRetakeKYCModal
      case 'exp':
        return handleRenderExpModal
      default:
        return undefined
    }
  }, [handleRenderExpModal, handleRenderRetakeKYCModal, modal])

  useDidMount(() => {
    handleGetStatus()
  })

  useFirestoreRoboyuStatus()

  useDidMount(() => {
    handleFetchRoboyuCustomizationStatus().then(
      (lv) => lv && setMaxExp(getRoboyuMaxExp(lv)),
    )
  })

  useDidUpdate(() => {
    if (maxExp > 0) {
      setProgress((exp / maxExp) * 100)
    }
  }, [maxExp])

  return (
    <StyledContainer>
      {handleRenderModal}
      {isVerified ? handleRenderSearchAccuracyDisabled : <></>}
      <StyledUpperSectionContainer>
        {handleRenderContent}
      </StyledUpperSectionContainer>
      {isVerified && handleRenderBanner}
      {handleRenderRoboyuFindings}
    </StyledContainer>
  )
}
